import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as d3 from 'd3';
import { Pane, Button, Spinner, TextInputField, SelectMenu, Badge, Heading, Text } from 'evergreen-ui';
import { useProject } from '../../context/ProjectContext';
import api from '../../services/api';
import SidePanel from './SidePanel';

const Graph = () => {
  const { currentProject } = useProject();
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  
  const [allData, setAllData] = useState({ nodes: [], links: [] });
  const [visibleData, setVisibleData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeLimit, setNodeLimit] = useState(50);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [nodeExpansions, setNodeExpansions] = useState({});
  const [nodePositions, setNodePositions] = useState({});
  const [expandingNodes, setExpandingNodes] = useState(new Set());

  // Constants
  const NODES_PER_EXPANSION = 10;

  // Severity colors
  const severityColors = {
    critical: '#EC4C47',
    high: '#D9822B',
    medium: '#EBAE39',
    low: '#47B881',
    info: '#14B5D0',
    none: '#66788A'
  };

  // Fetch graph data
  const fetchData = useCallback(async () => {
    if (!currentProject?.id) return;

    try {
      setLoading(true);
      const response = await api.get(`/projects/${currentProject.id}/graph`, {
        params: { limit: nodeLimit }
      });

      const { nodes, links } = response.data.graph;
      setAllData({ nodes, links });
      
      // Reset expansions when data changes
      setExpandedNodes(new Set());
      setNodeExpansions({});
      
    } catch (error) {
      console.error('Error fetching graph data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentProject?.id, nodeLimit]);

  // Calculate visible nodes based on expansions
  const calculateVisibleData = useCallback(() => {
    if (!allData.nodes.length) return { nodes: [], links: [] };

    // Start with project nodes + first 10 others
    const projectNodes = allData.nodes.filter(n => n.type === 'project');
    const otherNodes = allData.nodes.filter(n => n.type !== 'project');
    
    let visibleNodes = [...projectNodes, ...otherNodes.slice(0, NODES_PER_EXPANSION)];
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));

    // Add expanded nodes
    expandedNodes.forEach(nodeId => {
      const expansion = nodeExpansions[nodeId];
      if (expansion?.visibleConnections) {
        expansion.visibleConnections.forEach(connectedId => {
          const node = allData.nodes.find(n => n.id === connectedId);
          if (node && !visibleNodeIds.has(connectedId)) {
            visibleNodes.push(node);
            visibleNodeIds.add(connectedId);
          }
        });
      }
    });

    // Filter links to only include visible nodes
    const visibleLinks = allData.links.filter(link => 
      visibleNodeIds.has(link.source) && visibleNodeIds.has(link.target)
    );

    return { nodes: visibleNodes, links: visibleLinks };
  }, [allData, expandedNodes, nodeExpansions]);

  // Get connections for a node
  const getConnections = useCallback((nodeId) => {
    const connections = new Set();
    allData.links.forEach(link => {
      if (link.source === nodeId) connections.add(link.target);
      if (link.target === nodeId) connections.add(link.source);
    });
    return Array.from(connections).filter(id => 
      !visibleData.nodes.some(n => n.id === id)
    );
  }, [allData.links, visibleData.nodes]);

  // Check if node has more connections
  const hasMoreConnections = useCallback((nodeId) => {
    const connections = getConnections(nodeId);
    const expansion = nodeExpansions[nodeId];
    if (!expansion) return connections.length > 0;
    return expansion.visibleConnections.length < connections.length;
  }, [getConnections, nodeExpansions]);

  // Expand a node - add more connected nodes to the graph
  const expandNode = useCallback((nodeId) => {
    console.log('Expanding node:', nodeId);
    
    // Add visual feedback
    setExpandingNodes(prev => new Set([...prev, nodeId]));
    
    const connections = getConnections(nodeId);
    console.log('Available connections:', connections);
    
    if (connections.length === 0) {
      console.log('No more connections to expand');
      setExpandingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
      return;
    }
    
    const currentExpansion = nodeExpansions[nodeId] || { 
      visibleConnections: [], 
      currentPage: 0 
    };
    
    // Get the next batch of connections to show
    const startIndex = currentExpansion.visibleConnections.length;
    const endIndex = startIndex + NODES_PER_EXPANSION;
    const newConnections = connections.slice(startIndex, endIndex);
    
    console.log('Adding new connections:', newConnections);
    
    if (newConnections.length === 0) {
      console.log('No new connections to add');
      setExpandingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
      return;
    }
    
    // Update node expansions state
    setNodeExpansions(prev => ({
      ...prev,
      [nodeId]: {
        ...currentExpansion,
        visibleConnections: [...currentExpansion.visibleConnections, ...newConnections],
        currentPage: currentExpansion.currentPage + 1
      }
    }));
    
    // Mark this node as expanded
    setExpandedNodes(prev => new Set([...prev, nodeId]));
    
    // Remove expanding feedback after a delay
    setTimeout(() => {
      setExpandingNodes(prev => {
        const newSet = new Set(prev);
        newSet.delete(nodeId);
        return newSet;
      });
    }, 500);
    
    console.log('Node expansion updated');
  }, [getConnections, nodeExpansions]);

  // Update visible data when expansions change
  useEffect(() => {
    setVisibleData(calculateVisibleData());
  }, [calculateVisibleData]);

  // Fetch data on mount and when parameters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Render graph
  useEffect(() => {
    if (!visibleData.nodes.length || !svgRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const svg = d3.select(svgRef.current);
    const width = container.offsetWidth;
    const height = container.offsetHeight;

    // Clear previous content
    svg.selectAll("*").remove();
    svg.attr("width", width).attr("height", height);

    // Create main group
    const g = svg.append("g");

    // Setup zoom
    const zoom = d3.zoom()
      .scaleExtent([0.1, 5])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });
    svg.call(zoom);

    // Create simulation data (copies to avoid D3 mutations)
    // Preserve positions of existing nodes, place new nodes near their parent
    const nodes = visibleData.nodes.map((d, i) => {
      // Check if we have stored position for this node
      const storedPosition = nodePositions[d.id];
      if (storedPosition) {
        return { ...d, x: storedPosition.x, y: storedPosition.y };
      }
      
      // If node already has position, keep it
      if (d.x !== undefined && d.y !== undefined) {
        return { ...d };
      }
      
      // For new nodes, try to place them near their connecting node
      let parentNode = null;
      let parentPosition = null;
      
      for (const [expandedNodeId, expansion] of Object.entries(nodeExpansions)) {
        if (expansion.visibleConnections.includes(d.id)) {
          parentPosition = nodePositions[expandedNodeId];
          if (parentPosition) {
            parentNode = { x: parentPosition.x, y: parentPosition.y };
            break;
          }
          // Fallback to find parent in current visible data
          parentNode = visibleData.nodes.find(n => n.id === expandedNodeId);
          if (parentNode) break;
        }
      }
      
      if (parentNode && parentNode.x !== undefined && parentNode.y !== undefined) {
        // Place new node near its parent with some random offset
        const angle = Math.random() * 2 * Math.PI;
        const distance = 100 + Math.random() * 50;
        return {
          ...d,
          x: parentNode.x + Math.cos(angle) * distance,
          y: parentNode.y + Math.sin(angle) * distance
        };
      }
      
      // Default circular positioning for nodes without parent
      const angle = i * 2 * Math.PI / visibleData.nodes.length;
      return {
        ...d,
        x: width/2 + Math.cos(angle) * 150,
        y: height/2 + Math.sin(angle) * 150
      };
    });

    const links = visibleData.links.map(d => ({ ...d }));

    // Create links
    const link = g.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(links)
      .enter().append("line")
      .attr("stroke", "#999")
      .attr("stroke-width", 2);

    // Create nodes
    const node = g.append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(nodes)
      .enter().append("g")
      .attr("class", "node")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        setSelectedNode(d);
      });

    // Add node visuals based on type
    
    // Add circles for most node types
    node.filter(d => !['country', 'org'].includes(d.type))
      .append("circle")
      .attr("r", d => {
        if (d.type === 'project') return 20;
        if (d.type === 'asn') return 12;
        return 8;
      })
      .attr("fill", d => {
        if (d.type === 'project') return '#8B5CF6';
        if (d.type === 'asn') return '#7C3AED';
        return severityColors[d.data?.severity || 'none'];
      })
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);

    // Add icons for country and org nodes
    node.filter(d => ['country', 'org'].includes(d.type))
      .append("image")
      .attr("href", d => {
        if (d.type === 'country') return '/country.png';
        if (d.type === 'org') return '/org.png';
        return '';
      })
      .attr("width", d => {
        if (d.type === 'country') return 24;
        if (d.type === 'org') return 24;
        return 24;
      })
      .attr("height", d => {
        if (d.type === 'country') return 24;
        if (d.type === 'org') return 24;
        return 24;
      })
      .attr("x", -12)
      .attr("y", -12)
      .style("pointer-events", "all");

    // Add expansion buttons
    node.filter(d => hasMoreConnections(d.id))
      .append("g")
      .attr("class", "expand-btn")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        expandNode(d.id);
      })
      .each(function(d) {
        const btn = d3.select(this);
        const isExpanding = expandingNodes.has(d.id);
        
        btn.append("circle")
          .attr("cx", 20)
          .attr("cy", -20)
          .attr("r", 8)
          .attr("fill", isExpanding ? "#FF9800" : "#4CAF50")
          .attr("stroke", "#fff")
          .attr("stroke-width", 2)
          .style("opacity", isExpanding ? 0.7 : 1);
        
        btn.append("text")
          .attr("x", 20)
          .attr("y", -16)
          .attr("text-anchor", "middle")
          .attr("font-size", "12px")
          .attr("font-weight", "bold")
          .attr("fill", "white")
          .text(isExpanding ? "..." : "+");
        
        // Add pulsing animation for expanding nodes
        if (isExpanding) {
          btn.select("circle")
            .transition()
            .duration(500)
            .ease(d3.easeLinear)
            .attr("r", 10)
            .transition()
            .duration(500)
            .ease(d3.easeLinear)
            .attr("r", 8)
            .on("end", function repeat() {
              if (expandingNodes.has(d.id)) {
                d3.select(this)
                  .transition()
                  .duration(500)
                  .ease(d3.easeLinear)
                  .attr("r", 10)
                  .transition()
                  .duration(500)
                  .ease(d3.easeLinear)
                  .attr("r", 8)
                  .on("end", repeat);
              }
            });
        }
      });

    // Add labels
    const labels = g.append("g")
      .attr("class", "labels")
      .selectAll("text")
      .data(nodes)
      .enter().append("text")
      .text(d => d.label)
      .attr("text-anchor", "middle")
      .attr("dy", "2em")
      .attr("font-size", "10px")
      .attr("font-family", "Arial, sans-serif")
      .attr("fill", "#333")
      .style("pointer-events", "none");

    // Create or update simulation
    if (simulationRef.current) {
      // If simulation exists, update it with new nodes and links
      simulationRef.current.nodes(nodes);
      simulationRef.current.force("link").links(links);
      simulationRef.current.alpha(0.3).restart(); // Gentle restart for new nodes
    } else {
      // Create new simulation
      simulationRef.current = d3.forceSimulation(nodes)
        .force("link", d3.forceLink(links).id(d => d.id).distance(120).strength(0.5))
        .force("charge", d3.forceManyBody().strength(-500))
        .force("center", d3.forceCenter(width / 2, height / 2).strength(0.1))
        .force("collision", d3.forceCollide().radius(35))
        .alpha(1)
        .alphaDecay(0.02);
    }

    // Update positions on tick
    simulationRef.current.on("tick", () => {
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);

      node.attr("transform", d => `translate(${d.x},${d.y})`);
      labels.attr("x", d => d.x).attr("y", d => d.y);
      
      // Store positions for persistence
      setNodePositions(prev => {
        const newPositions = { ...prev };
        nodes.forEach(node => {
          if (node.x !== undefined && node.y !== undefined) {
            newPositions[node.id] = { x: node.x, y: node.y };
          }
        });
        return newPositions;
      });
    });

    // Add drag behavior
    const drag = d3.drag()
      .on("start", (event, d) => {
        if (!event.active) simulationRef.current.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulationRef.current.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    node.call(drag);

    // Auto-fit after simulation settles
    setTimeout(() => {
      const bounds = {
        minX: Math.min(...nodes.map(d => d.x)) - 50,
        maxX: Math.max(...nodes.map(d => d.x)) + 50,
        minY: Math.min(...nodes.map(d => d.y)) - 50,
        maxY: Math.max(...nodes.map(d => d.y)) + 50
      };
      
      const graphWidth = bounds.maxX - bounds.minX;
      const graphHeight = bounds.maxY - bounds.minY;
      
      if (graphWidth > 0 && graphHeight > 0) {
        const scale = Math.min(
          (width * 0.8) / graphWidth,
          (height * 0.8) / graphHeight,
          1
        );
        
        const centerX = width / 2 - (bounds.minX + graphWidth / 2) * scale;
        const centerY = height / 2 - (bounds.minY + graphHeight / 2) * scale;
        
        svg.transition().duration(750).call(
          zoom.transform,
          d3.zoomIdentity.translate(centerX, centerY).scale(scale)
        );
      }
    }, 2000);

    // Cleanup
    return () => {
      if (simulationRef.current) simulationRef.current.stop();
    };

  }, [visibleData, hasMoreConnections, expandNode]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      // Trigger re-render on resize
      setVisibleData(prev => ({ ...prev }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!currentProject) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh" flexDirection="column" gap={16}>
        <Heading size={700} color="muted">No Project Selected</Heading>
        <Text size={400} color="muted" textAlign="center">
          Select a project to view the graph visualization.
        </Text>
      </Pane>
    );
  }

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh">
        <Spinner />
      </Pane>
    );
  }

  return (
    <Pane height="100vh" display="flex" position="relative" overflow="hidden">
      {/* Sidebar */}
      <Pane padding={16} background="tint1" width={240} height="100vh" overflowY="auto">
        <Heading size={500} marginBottom={16}>Graph Controls</Heading>
        
        <TextInputField
          label="Node Limit"
          type="number"
          value={nodeLimit}
          onChange={e => setNodeLimit(parseInt(e.target.value) || 10)}
          min={10}
          max={500}
          marginBottom={16}
        />

        <Pane marginBottom={16}>
          <Text size={400} color="muted" marginBottom={8}>
            Showing {visibleData.nodes.length} of {allData.nodes.length} nodes
          </Text>
          <Text size={300} color="muted">
            Click "+" on nodes to expand connections
          </Text>
        </Pane>

        <Button
          width="100%"
          onClick={() => {
            setExpandedNodes(new Set());
            setNodeExpansions({});
            setNodePositions({});
            setExpandingNodes(new Set());
            // Force re-render to reset positions
            setVisibleData(prev => ({ ...prev }));
          }}
        >
          Reset Expansions
        </Button>
      </Pane>

      {/* Main Graph Area */}
      <Pane 
        ref={containerRef}
        flex={1} 
        position="relative" 
        height="100vh"
        backgroundColor="white"
        overflow="hidden"
      >
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ cursor: 'grab' }}
          onClick={() => setSelectedNode(null)}
        />
        
        {/* Zoom Controls */}
        <Pane position="absolute" top={16} right={16} display="flex" flexDirection="column" gap={8}>
          <Button size="small" onClick={() => {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(300).call(
              d3.zoom().transform,
              d3.zoomIdentity.scale(1.5)
            );
          }}>+</Button>
          
          <Button size="small" onClick={() => {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(300).call(
              d3.zoom().transform,
              d3.zoomIdentity.scale(0.7)
            );
          }}>-</Button>
          
          <Button size="small" onClick={() => {
            const svg = d3.select(svgRef.current);
            svg.transition().duration(300).call(
              d3.zoom().transform,
              d3.zoomIdentity
            );
          }}>Reset</Button>
        </Pane>
      </Pane>

      {/* Side Panel */}
      {selectedNode && (
        <SidePanel 
          selectedNode={selectedNode} 
          onClose={() => setSelectedNode(null)} 
        />
      )}
    </Pane>
  );
};

export default Graph;